import { SidebarActionReveal } from '@affine/core/modules/app-sidebar/views/action-reveal';
import clsx from 'clsx';
import type { ComponentProps } from 'react';

import { AnimatedDisclosure } from '../animation/animated-disclosure';
import * as styles from './index.css';

export { SIDEBAR_DISCLOSURE_HEADER_HEIGHT } from './index.css';

/** Hub row chrome plus the canonical disclosure; actions remain separate buttons. */
export function SidebarDisclosureItem({
  title,
  actions,
  expanded,
  onExpandedChange,
  headerProps,
  headerClassName,
  triggerClassName,
  size = 'section',
  hoverActions = false,
  ...props
}: Omit<
  ComponentProps<typeof AnimatedDisclosure>,
  'ref' | 'expanded' | 'onExpandedChange'
> & {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  size?: 'section' | 'subsection';
  hoverActions?: boolean;
}) {
  return (
    <AnimatedDisclosure
      {...props}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      headerProps={{
        ...headerProps,
        onClick: event => {
          headerProps?.onClick?.(event);
          if (
            event.defaultPrevented ||
            (event.target instanceof Element &&
              event.target.closest('button, a, input, select, textarea'))
          )
            return;
          onExpandedChange(!expanded);
        },
      }}
      title={<span className={styles.label}>{title}</span>}
      actions={
        !hoverActions && actions ? (
          <div className={styles.actions}>{actions}</div>
        ) : null
      }
      renderHeader={
        hoverActions
          ? header => (
              <SidebarActionReveal
                className={styles.hoverHeader}
                actions={actions}
                onClick={event => {
                  // Inner header/trigger clicks already own disclosure. Only
                  // the reveal's empty surface needs the same background action.
                  if (
                    event.target === event.currentTarget ||
                    (event.target instanceof Element &&
                      event.target.hasAttribute(
                        'data-sidebar-action-reveal-content'
                      ))
                  )
                    onExpandedChange(!expanded);
                }}
              >
                {header}
              </SidebarActionReveal>
            )
          : undefined
      }
      headerClassName={clsx(
        styles.header,
        size === 'subsection' && styles.subsectionHeader,
        headerClassName,
        hoverActions && styles.revealedHeader
      )}
      triggerClassName={clsx(
        styles.trigger,
        size === 'subsection' && styles.subsectionTrigger,
        triggerClassName
      )}
    />
  );
}
